import { ContentModalContentType, ContentTypeForComparison } from 'domains/content/types';
import { Segment, SegmentTableItemType } from 'domains/segments/types';
import { Folder } from 'models/types';

import useDeleteContentFolder from './mutations/useDeleteContentFolder';
import useDeleteDirectMail from './mutations/useDeleteDirectMail';
import useDeleteLandingPage from './mutations/useDeleteLandingPage';
import useDeleteSegment from './mutations/useDeleteSegment';
import useDeleteSegmentFolder from './mutations/useDeleteSegmentFolder';
import useDeleteSms from './mutations/useDeleteSms'; // Add this import
import useDeleteTemplate from './mutations/useDeleteTemplate';
import useDeleteVoiceContent from './mutations/useDeleteVoiceContent';
import useContentQueryManager from './useContentQueryParams';
import useSegmentQueryManager from './useSegmentQueryManager';

type Props = {
    activeItem: ContentModalContentType | Segment | Folder;
    refetchTableData: () => void;
    resetActiveItem: () => void;
    setErrorMessage: (error: string) => void;
    setSuccessMessage: (message: string) => void;
};

const useDeleteTableItem = ({
    activeItem,
    refetchTableData,
    resetActiveItem,
    setErrorMessage,
    setSuccessMessage,
}: Props) => {
    const { changeViewParams: changeContentTableViewParams, folderId: contentTableFolderId } = useContentQueryManager();

    const { changeViewParams: changeSegmentTableViewParams } = useSegmentQueryManager();

    const onError = (error: Error) => {
        setErrorMessage(error.message);
        resetActiveItem();
    };

    const onSuccess = () => {
        setSuccessMessage(`"${activeItem?.name}" successfully deleted.`);
        if (activeItem.type === ContentTypeForComparison.FOLDER && contentTableFolderId !== -1) {
            changeContentTableViewParams({ newFolderId: -1 });
        }
        if (activeItem.type === SegmentTableItemType.FOLDER) {
            changeSegmentTableViewParams({ newFolderId: -1 });
        }
        refetchTableData();
        resetActiveItem();
    };

    const itemToDeleteId = activeItem ? `${activeItem?.id}` : null;

    // Content table methods
    const {
        isLoading: isDeleteDirectMailLoading,
        mutate: deleteDirectMail,
        isSuccess: isDeleteDirectMailSuccess,
    } = useDeleteDirectMail(itemToDeleteId, contentTableFolderId, { onError, onSuccess });

    const {
        isLoading: isDeleteLandingPageLoading,
        mutate: deleteLandingPage,
        isSuccess: isDeleteLandingPageSuccess,
    } = useDeleteLandingPage(itemToDeleteId, contentTableFolderId, { onError, onSuccess });

    const {
        isLoading: isDeleteTemplateLoading,
        mutate: deleteTemplate,
        isSuccess: isDeleteTemplateSuccess,
    } = useDeleteTemplate(itemToDeleteId, contentTableFolderId, { onError, onSuccess });

    const {
        isLoading: isDeleteSmsLoading,
        mutate: deleteSms,
        isSuccess: isDeleteSmsSuccess,
    } = useDeleteSms(itemToDeleteId, contentTableFolderId, { onError, onSuccess });

    const {
        isLoading: isDeleteContentFolderLoading,
        mutate: deleteContentFolder,
        isSuccess: isDeleteContentFolderSuccess,
    } = useDeleteContentFolder(itemToDeleteId, { onError, onSuccess });

    const {
        isLoading: isDeleteVoiceContentLoading,
        mutate: deleteVoiceContent,
        isSuccess: isDeleteVoiceContentSuccess,
    } = useDeleteVoiceContent(itemToDeleteId, contentTableFolderId, { onError, onSuccess });

    // Segment table methods
    const {
        isLoading: isDeleteSegmentLoading,
        mutate: deleteSegment,
        isSuccess: isDeleteSegmentSuccess,
    } = useDeleteSegment(`${activeItem?.id}`, { onError, onSuccess });

    const {
        isLoading: isDeleteSegmentFolderLoading,
        mutate: deleteSegmentFolder,
        isSuccess: isDeleteSegmentFolderSuccess,
    } = useDeleteSegmentFolder(itemToDeleteId, { onError, onSuccess });

    const getDeleteItemFunction = () => {
        switch (activeItem?.type) {
            case ContentTypeForComparison.DIRECT_MAIL:
                return deleteDirectMail;
            case ContentTypeForComparison.EMAIL:
                return deleteTemplate;
            case ContentTypeForComparison.LANDING_PAGE:
                return deleteLandingPage;
            case ContentTypeForComparison.SMS:
                return deleteSms;
            case ContentTypeForComparison.FOLDER:
                return deleteContentFolder;
            case ContentTypeForComparison.VOICE:
                return deleteVoiceContent;
            case SegmentTableItemType.AUDIENCE:
                return deleteSegment;
            case SegmentTableItemType.FOLDER:
                return deleteSegmentFolder;
            default:
                return () => {};
        }
    };

    const isLoading =
        isDeleteDirectMailLoading ||
        isDeleteLandingPageLoading ||
        isDeleteTemplateLoading ||
        isDeleteContentFolderLoading ||
        isDeleteSegmentLoading ||
        isDeleteSegmentFolderLoading ||
        isDeleteSmsLoading ||
        isDeleteVoiceContentLoading;

    const isSuccess =
        isDeleteDirectMailSuccess ||
        isDeleteLandingPageSuccess ||
        isDeleteTemplateSuccess ||
        isDeleteContentFolderSuccess ||
        isDeleteSegmentSuccess ||
        isDeleteSegmentFolderSuccess ||
        isDeleteSmsSuccess ||
        isDeleteVoiceContentSuccess;

    return { getDeleteItemFunction, isLoading, isSuccess };
};

export default useDeleteTableItem;
